/**
 * The figures of a reading document (D-163): TEF CE section 3, question 22, compares four graphs
 * (« Graphique 1 » to « Graphique 4 ») with a comment. One small column chart per series, all on
 * the same scale so that they can be compared, as on the test. Plain SVG with tokens only; each
 * chart is an image with its values in words, and the data table is one click away.
 */

import { type PassageTable, tableSeries } from "@agencyos/core";
import { useTranslation } from "react-i18next";

import "./charts.css";

const W = 168;
const H = 128;
const PAD = { top: 16, right: 6, bottom: 20, left: 6 };

function columnPath(x: number, y: number, width: number, height: number): string {
  // 4px rounded data end, square on the baseline
  const r = Math.min(4, width / 2, height);
  const bottom = y + height;
  return `M${x},${bottom} V${y + r} Q${x},${y} ${x + r},${y} H${x + width - r} Q${x + width},${y} ${x + width},${y + r} V${bottom} Z`;
}

export function TableCharts({ table, lang = "fr" }: { table: PassageTable | null; lang?: string }) {
  const { t } = useTranslation();
  const series = tableSeries(table);
  if (!table || !series.length) return null;
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  return (
    <section className="ac-charts" lang={lang} aria-label={table.caption || t("conformity.charts.title")} data-testid="table-charts">
      {table.caption && <p className="ac-charts__caption">{table.caption}</p>}
      <div className="ac-charts__grid">
        {series.map((s) => {
          const span = s.max - s.min || 1;
          const slot = plotW / Math.max(s.points.length, 1);
          const barW = Math.max(6, slot - 8);
          const described = s.points.map((p) => `${p.x} : ${p.y ?? "—"}`).join(", ");
          return (
            <figure key={s.label} className="ac-chart">
              <figcaption className="ac-chart__title">{s.label}</figcaption>
              <svg className="ac-chart__svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${s.label} — ${described}`}>
                <line className="ac-chart__base" x1={PAD.left} x2={W - PAD.right} y1={PAD.top + plotH} y2={PAD.top + plotH} />
                {s.points.map((p, i) => {
                  const x = PAD.left + i * slot + (slot - barW) / 2;
                  const value = p.y ?? 0;
                  const h = Math.max(((value - s.min) / span) * plotH, p.y === null ? 0 : 1);
                  const y = PAD.top + plotH - h;
                  return (
                    <g key={p.x}>
                      <title>{`${p.x} : ${p.y ?? "—"}`}</title>
                      <rect className="ac-chart__hit" x={PAD.left + i * slot} y={PAD.top} width={slot} height={plotH} />
                      {p.y !== null && <path className="ac-chart__bar" d={columnPath(x, y, barW, h)} />}
                      <text className="ac-chart__value" x={x + barW / 2} y={y - 4} textAnchor="middle">
                        {p.y ?? "—"}
                      </text>
                      <text className="ac-chart__axis" x={x + barW / 2} y={H - 6} textAnchor="middle">
                        {p.x.length > 4 ? `'${p.x.slice(-2)}` : p.x}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </figure>
          );
        })}
      </div>
      <details className="ac-charts__data">
        <summary>{t("conformity.charts.showData")}</summary>
        <div className="ac-charts__table-wrap">
          <table className="ac-charts__table">
            {table.caption && <caption>{table.caption}</caption>}
            <thead>
              <tr>
                {table.columns.map((c) => (
                  <th key={c} scope="col">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) =>
                    c === 0 ? (
                      <th key={c} scope="row">
                        {cell}
                      </th>
                    ) : (
                      <td key={c} className="ao-num">
                        {cell}
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}
